import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { toast } from "sonner";
import { Warehouse, MapPin, Package, ArrowRightLeft, AlertTriangle, Plus, Loader2, Trash2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EXP_COLORS = { expired: "bg-red-100 text-red-800", critical: "bg-orange-100 text-orange-800", warning: "bg-yellow-100 text-yellow-800", ok: "bg-emerald-100 text-emerald-800" };
const EXP_LABEL = { expired: "Expirado", critical: "Crítico ≤7d", warning: "Aviso ≤30d", ok: "OK" };

function Kpi({ label, value, color = "#0b1f3a", icon: Icon }) {
  return (
    <div className="bg-white rounded-lg border p-4">
      <div className="flex items-center gap-2 text-xs text-slate-500 uppercase tracking-wide">
        {Icon && <Icon className="h-3.5 w-3.5" />}{label}
      </div>
      <div className="text-3xl font-black mt-1" style={{ color }}>{value ?? "-"}</div>
    </div>
  );
}

export default function Wms() {
  const [tab, setTab] = useState("lots");
  const [summary, setSummary] = useState(null);
  const [lots, setLots] = useState([]);
  const [locations, setLocations] = useState([]);
  const [movements, setMovements] = useState([]);
  const [expiring, setExpiring] = useState([]);
  const [parts, setParts] = useState([]);
  const [loading, setLoading] = useState(false);

  // dialogs
  const [showLotDlg, setShowLotDlg] = useState(false);
  const [showLocDlg, setShowLocDlg] = useState(false);
  const [showMoveDlg, setShowMoveDlg] = useState(false);
  const [moveLot, setMoveLot] = useState(null);

  const [lotForm, setLotForm] = useState({ part_id: "", lot_number: "", quantity: "", unit: "kg", location_code: "", expiry_date: "", supplier: "", supplier_lot: "", cost_per_unit: "" });
  const [locForm, setLocForm] = useState({ code: "", zone: "", aisle: "", rack: "", level: "", capacity_kg: "", notes: "" });
  const [moveForm, setMoveForm] = useState({ movement_type: "transfer", quantity: 0, to_location: "", reason: "" });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, l, loc, mv, ex, p] = await Promise.all([
        axios.get(`${API}/wms/summary`),
        axios.get(`${API}/wms/lots`),
        axios.get(`${API}/wms/locations`),
        axios.get(`${API}/wms/movements`),
        axios.get(`${API}/wms/alerts/expiry?days_window=30`),
        axios.get(`${API}/parts`),
      ]);
      setSummary(s.data); setLots(l.data); setLocations(loc.data); setMovements(mv.data); setExpiring(ex.data); setParts(p.data);
    } catch (e) { toast.error("Erro a carregar WMS"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const createLot = async () => {
    if (!lotForm.part_id || !lotForm.lot_number || !lotForm.quantity) { toast.error("Peça, lote e quantidade obrigatórios"); return; }
    try {
      const payload = { ...lotForm, quantity: parseFloat(lotForm.quantity),
        cost_per_unit: lotForm.cost_per_unit ? parseFloat(lotForm.cost_per_unit) : null,
        location_code: lotForm.location_code || null, expiry_date: lotForm.expiry_date || null };
      await axios.post(`${API}/wms/lots`, payload);
      toast.success("Lote criado");
      setShowLotDlg(false); setLotForm({ part_id: "", lot_number: "", quantity: "", unit: "kg", location_code: "", expiry_date: "", supplier: "", supplier_lot: "", cost_per_unit: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const createLocation = async () => {
    if (!locForm.code || !locForm.zone) { toast.error("Código e zona obrigatórios"); return; }
    try {
      const payload = { ...locForm, capacity_kg: locForm.capacity_kg ? parseFloat(locForm.capacity_kg) : null };
      await axios.post(`${API}/wms/locations`, payload);
      toast.success("Localização criada");
      setShowLocDlg(false); setLocForm({ code: "", zone: "", aisle: "", rack: "", level: "", capacity_kg: "", notes: "" });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const deleteLocation = async (code) => {
    if (!window.confirm(`Apagar localização ${code}?`)) return;
    try { await axios.delete(`${API}/wms/locations/${code}`); toast.success("Apagada"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  const openMove = (lot, type = "transfer") => {
    setMoveLot(lot);
    setMoveForm({ movement_type: type, quantity: type === "transfer" ? 0 : lot.quantity, to_location: "", reason: "" });
    setShowMoveDlg(true);
  };

  const submitMovement = async () => {
    if (moveForm.movement_type === "transfer" && !moveForm.to_location) { toast.error("Localização destino obrigatória"); return; }
    if (moveForm.movement_type !== "transfer" && !moveForm.quantity) { toast.error("Quantidade obrigatória"); return; }
    try {
      await axios.post(`${API}/wms/movement`, {
        lot_id: moveLot.id,
        movement_type: moveForm.movement_type,
        quantity: parseFloat(moveForm.quantity || 0),
        to_location: moveForm.to_location || null,
        reason: moveForm.reason || null,
      });
      toast.success("Movimento registado");
      setShowMoveDlg(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erro"); }
  };

  return (
    <div className="space-y-4" data-testid="wms-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Warehouse className="h-8 w-8 text-cyan-600" /> WMS Avançado
          </h1>
          <p className="text-sm text-muted-foreground mt-1">Gestão de lotes, localizações, FIFO e validades.</p>
        </div>
      </div>

      {/* KPIs */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Kpi label="Lotes ativos" value={summary.total_lots} icon={Package} />
          <Kpi label="Localizações" value={summary.total_locations} icon={MapPin} />
          <Kpi label="A expirar (≤7d)" value={summary.critical} color={summary.critical ? "#dc2626" : "#0b1f3a"} icon={AlertTriangle} />
          <Kpi label="Expirados" value={summary.expired} color={summary.expired ? "#7f1d1d" : "#0b1f3a"} icon={AlertTriangle} />
        </div>
      )}

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="lots" data-testid="tab-lots">Lotes ({lots.length})</TabsTrigger>
          <TabsTrigger value="locations" data-testid="tab-locations">Localizações ({locations.length})</TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements">Movimentações</TabsTrigger>
          <TabsTrigger value="expiry" data-testid="tab-expiry">Validades {expiring.length ? `(${expiring.length})` : ""}</TabsTrigger>
        </TabsList>

        {/* -------------- LOTES -------------- */}
        <TabsContent value="lots">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="font-semibold">Lotes ativos ({lots.length})</div>
              <Button size="sm" onClick={() => setShowLotDlg(true)} data-testid="new-lot-btn" className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="h-4 w-4 mr-1" /> Novo lote
              </Button>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Lote</th>
                    <th className="text-left p-2">Material/Peça</th>
                    <th className="text-right p-2">Quantidade</th>
                    <th className="text-left p-2">Localização</th>
                    <th className="text-left p-2">Validade</th>
                    <th className="text-left p-2">Fornecedor</th>
                    <th className="text-right p-2">Recebido</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {lots.length === 0 && <tr><td colSpan="8" className="text-center p-6 text-slate-400">Sem lotes ativos.</td></tr>}
                  {lots.map(l => (
                    <tr key={l.id} className="border-t hover:bg-slate-50" data-testid={`lot-row-${l.id}`}>
                      <td className="p-2 font-mono font-semibold">{l.lot_number}</td>
                      <td className="p-2">
                        <div className="font-medium">{l.part_code}</div>
                        <div className="text-xs text-slate-500 truncate max-w-xs">{l.part_description}</div>
                      </td>
                      <td className="p-2 text-right font-mono">{l.quantity} <span className="text-slate-500 text-xs">{l.unit}</span></td>
                      <td className="p-2">{l.location_code || <span className="text-slate-400">—</span>}</td>
                      <td className="p-2">
                        {l.expiry_date ? (
                          <div className="flex flex-col">
                            <span className="text-xs">{l.expiry_date.substring(0, 10)}</span>
                            {l.expiry_status && <span className={`text-[10px] px-1.5 py-0.5 rounded self-start ${EXP_COLORS[l.expiry_status]}`}>{EXP_LABEL[l.expiry_status]} {l.days_to_expiry !== null ? `(${l.days_to_expiry}d)` : ""}</span>}
                          </div>
                        ) : <span className="text-slate-400">—</span>}
                      </td>
                      <td className="p-2 text-xs">{l.supplier || <span className="text-slate-400">—</span>}</td>
                      <td className="p-2 text-right text-xs text-slate-500">{l.received_at ? new Date(l.received_at).toLocaleDateString("pt-PT") : ""}</td>
                      <td className="p-2 flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => openMove(l, "transfer")} title="Transferir" data-testid={`transfer-${l.id}`}>
                          <ArrowRightLeft className="h-3.5 w-3.5" />
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => openMove(l, "consumption")} title="Consumir" data-testid={`consume-${l.id}`}>
                          <Package className="h-3.5 w-3.5" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* -------------- LOCALIZAÇÕES -------------- */}
        <TabsContent value="locations">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b flex items-center justify-between">
              <div className="font-semibold">Localizações ({locations.length})</div>
              <Button size="sm" onClick={() => setShowLocDlg(true)} data-testid="new-location-btn" className="bg-cyan-600 hover:bg-cyan-700">
                <Plus className="h-4 w-4 mr-1" /> Nova localização
              </Button>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 p-4">
              {locations.length === 0 && <div className="col-span-full text-center py-8 text-slate-400">Sem localizações configuradas.</div>}
              {locations.map(loc => (
                <div key={loc.code} className="border rounded-lg p-3 hover:shadow-md transition" data-testid={`location-${loc.code}`}>
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-mono font-bold text-cyan-700">{loc.code}</div>
                      <div className="text-xs text-slate-600 mt-0.5">{loc.zone} {loc.aisle && `· ${loc.aisle}`} {loc.rack && `· ${loc.rack}`} {loc.level && `· ${loc.level}`}</div>
                    </div>
                    <button onClick={() => deleteLocation(loc.code)} className="text-red-400 hover:text-red-600" title="Apagar">
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="mt-2 flex items-baseline gap-2">
                    <span className="text-2xl font-black">{loc.current_qty || 0}</span>
                    <span className="text-xs text-slate-500">/ {loc.capacity_kg || "∞"} kg · {loc.lots_count || 0} lotes</span>
                  </div>
                  {loc.notes && <div className="text-xs text-slate-500 mt-1">{loc.notes}</div>}
                </div>
              ))}
            </div>
          </div>
        </TabsContent>

        {/* -------------- MOVIMENTAÇÕES -------------- */}
        <TabsContent value="movements">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b font-semibold">Movimentações (últimos 30 dias) — {movements.length}</div>
            <div className="overflow-x-auto max-h-[600px]">
              <table className="w-full text-sm">
                <thead className="bg-slate-50 sticky top-0">
                  <tr>
                    <th className="text-left p-2">Data</th>
                    <th className="text-left p-2">Tipo</th>
                    <th className="text-left p-2">Lote</th>
                    <th className="text-left p-2">Peça</th>
                    <th className="text-right p-2">Qtd</th>
                    <th className="text-left p-2">De → Para</th>
                    <th className="text-left p-2">Utilizador</th>
                    <th className="text-left p-2">Motivo</th>
                  </tr>
                </thead>
                <tbody>
                  {movements.length === 0 && <tr><td colSpan="8" className="text-center p-6 text-slate-400">Sem movimentos.</td></tr>}
                  {movements.map(m => (
                    <tr key={m.id} className="border-t">
                      <td className="p-2 text-xs whitespace-nowrap">{new Date(m.timestamp).toLocaleString("pt-PT")}</td>
                      <td className="p-2"><span className="text-xs px-2 py-0.5 rounded bg-slate-100">{m.type}</span></td>
                      <td className="p-2 font-mono text-xs">{m.lot_number || "-"}</td>
                      <td className="p-2 text-xs">{m.part_code}</td>
                      <td className="p-2 text-right font-mono">{m.quantity}</td>
                      <td className="p-2 text-xs">{m.from_location || "-"} → {m.to_location || "-"}</td>
                      <td className="p-2 text-xs">{m.user_code}</td>
                      <td className="p-2 text-xs text-slate-500">{m.reason || ""}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>

        {/* -------------- VALIDADES -------------- */}
        <TabsContent value="expiry">
          <div className="bg-white border rounded-lg overflow-hidden">
            <div className="p-3 border-b font-semibold">Lotes a expirar nos próximos 30 dias — {expiring.length}</div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <th className="text-left p-2">Estado</th>
                    <th className="text-left p-2">Lote</th>
                    <th className="text-left p-2">Peça</th>
                    <th className="text-right p-2">Quantidade</th>
                    <th className="text-left p-2">Localização</th>
                    <th className="text-left p-2">Validade</th>
                    <th className="text-right p-2">Dias</th>
                  </tr>
                </thead>
                <tbody>
                  {expiring.length === 0 && <tr><td colSpan="7" className="text-center p-6 text-emerald-600">Sem lotes a expirar. 🎉</td></tr>}
                  {expiring.map(l => (
                    <tr key={l.id} className="border-t" data-testid={`expiry-${l.id}`}>
                      <td className="p-2"><span className={`text-xs px-2 py-0.5 rounded ${EXP_COLORS[l.expiry_status]}`}>{EXP_LABEL[l.expiry_status]}</span></td>
                      <td className="p-2 font-mono">{l.lot_number}</td>
                      <td className="p-2">{l.part_code}</td>
                      <td className="p-2 text-right font-mono">{l.quantity} {l.unit}</td>
                      <td className="p-2">{l.location_code || "-"}</td>
                      <td className="p-2 text-xs">{l.expiry_date?.substring(0, 10)}</td>
                      <td className="p-2 text-right font-bold" style={{ color: l.days_to_expiry < 0 ? "#7f1d1d" : l.days_to_expiry <= 7 ? "#dc2626" : "#d97706" }}>{l.days_to_expiry}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog novo lote */}
      <Dialog open={showLotDlg} onOpenChange={setShowLotDlg}>
        <DialogContent className="max-w-lg" data-testid="lot-dialog">
          <DialogHeader><DialogTitle>Novo lote de material</DialogTitle><DialogDescription>Regista uma entrada de matéria-prima com nº lote, quantidade, localização e validade.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div className="col-span-2">
              <Label>Peça / Material *</Label>
              <Select value={lotForm.part_id} onValueChange={(v) => setLotForm({ ...lotForm, part_id: v })}>
                <SelectTrigger data-testid="lot-part-select"><SelectValue placeholder="Escolhe..." /></SelectTrigger>
                <SelectContent>
                  {parts.map(p => <SelectItem key={p.id} value={p.id}>{p.code} — {p.description}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Nº Lote *</Label>
              <Input value={lotForm.lot_number} onChange={(e) => setLotForm({ ...lotForm, lot_number: e.target.value })} data-testid="lot-number" />
            </div>
            <div>
              <Label>Quantidade *</Label>
              <div className="flex gap-1">
                <Input type="number" step="0.001" value={lotForm.quantity} onChange={(e) => setLotForm({ ...lotForm, quantity: e.target.value })} data-testid="lot-qty" />
                <Select value={lotForm.unit} onValueChange={(v) => setLotForm({ ...lotForm, unit: v })}>
                  <SelectTrigger className="w-20"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="kg">kg</SelectItem>
                    <SelectItem value="un">un</SelectItem>
                    <SelectItem value="m">m</SelectItem>
                    <SelectItem value="L">L</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div>
              <Label>Localização</Label>
              <Select value={lotForm.location_code} onValueChange={(v) => setLotForm({ ...lotForm, location_code: v })}>
                <SelectTrigger data-testid="lot-location"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent>
                  {locations.map(l => <SelectItem key={l.code} value={l.code}>{l.code} · {l.zone}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Validade</Label>
              <Input type="date" value={lotForm.expiry_date} onChange={(e) => setLotForm({ ...lotForm, expiry_date: e.target.value })} data-testid="lot-expiry" />
            </div>
            <div>
              <Label>Fornecedor</Label>
              <Input value={lotForm.supplier} onChange={(e) => setLotForm({ ...lotForm, supplier: e.target.value })} />
            </div>
            <div>
              <Label>Lote fornecedor</Label>
              <Input value={lotForm.supplier_lot} onChange={(e) => setLotForm({ ...lotForm, supplier_lot: e.target.value })} />
            </div>
            <div>
              <Label>Custo € / unidade</Label>
              <Input type="number" step="0.001" value={lotForm.cost_per_unit} onChange={(e) => setLotForm({ ...lotForm, cost_per_unit: e.target.value })} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLotDlg(false)}>Cancelar</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={createLot} data-testid="save-lot-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog nova localização */}
      <Dialog open={showLocDlg} onOpenChange={setShowLocDlg}>
        <DialogContent data-testid="location-dialog">
          <DialogHeader><DialogTitle>Nova localização</DialogTitle><DialogDescription>Define uma posição no armazém (zona, corredor, estante, nível) para arrumar lotes.</DialogDescription></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Código *</Label><Input value={locForm.code} onChange={(e) => setLocForm({ ...locForm, code: e.target.value.toUpperCase() })} placeholder="A-01-03-B" data-testid="loc-code" /></div>
            <div><Label>Zona *</Label><Input value={locForm.zone} onChange={(e) => setLocForm({ ...locForm, zone: e.target.value })} placeholder="Armazém MP" data-testid="loc-zone" /></div>
            <div><Label>Corredor</Label><Input value={locForm.aisle} onChange={(e) => setLocForm({ ...locForm, aisle: e.target.value })} /></div>
            <div><Label>Estante</Label><Input value={locForm.rack} onChange={(e) => setLocForm({ ...locForm, rack: e.target.value })} /></div>
            <div><Label>Nível</Label><Input value={locForm.level} onChange={(e) => setLocForm({ ...locForm, level: e.target.value })} /></div>
            <div><Label>Capacidade (kg)</Label><Input type="number" value={locForm.capacity_kg} onChange={(e) => setLocForm({ ...locForm, capacity_kg: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notas</Label><Input value={locForm.notes} onChange={(e) => setLocForm({ ...locForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowLocDlg(false)}>Cancelar</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={createLocation} data-testid="save-loc-btn">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog movimento (transferir/consumir) */}
      <Dialog open={showMoveDlg} onOpenChange={setShowMoveDlg}>
        <DialogContent data-testid="move-dialog">
          <DialogHeader><DialogTitle>{moveForm.movement_type === "transfer" ? "Transferir lote" : moveForm.movement_type === "consumption" ? "Consumir lote" : "Movimento"}</DialogTitle><DialogDescription>Regista transferência, consumo, sucata ou ajuste do lote seleccionado.</DialogDescription></DialogHeader>
          {moveLot && (
            <>
              <div className="text-sm text-slate-600 mb-2">
                Lote <span className="font-mono font-semibold">{moveLot.lot_number}</span> · {moveLot.part_code} · {moveLot.quantity} {moveLot.unit} · loc atual: <span className="font-mono">{moveLot.location_code || "—"}</span>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="col-span-2">
                  <Label>Tipo</Label>
                  <Select value={moveForm.movement_type} onValueChange={(v) => setMoveForm({ ...moveForm, movement_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="transfer">Transferência</SelectItem>
                      <SelectItem value="consumption">Consumo</SelectItem>
                      <SelectItem value="scrap">Sucata</SelectItem>
                      <SelectItem value="adjustment">Ajuste (±)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {moveForm.movement_type === "transfer" ? (
                  <div className="col-span-2">
                    <Label>Nova localização *</Label>
                    <Select value={moveForm.to_location} onValueChange={(v) => setMoveForm({ ...moveForm, to_location: v })}>
                      <SelectTrigger data-testid="move-to-location"><SelectValue placeholder="Escolhe..." /></SelectTrigger>
                      <SelectContent>
                        {locations.filter(l => l.code !== moveLot.location_code).map(l => <SelectItem key={l.code} value={l.code}>{l.code} · {l.zone}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                ) : (
                  <div>
                    <Label>Quantidade *</Label>
                    <Input type="number" step="0.001" value={moveForm.quantity} onChange={(e) => setMoveForm({ ...moveForm, quantity: e.target.value })} data-testid="move-qty" />
                  </div>
                )}
                <div className={moveForm.movement_type === "transfer" ? "col-span-2" : ""}>
                  <Label>Motivo/Notas</Label>
                  <Input value={moveForm.reason} onChange={(e) => setMoveForm({ ...moveForm, reason: e.target.value })} />
                </div>
              </div>
            </>
          )}
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowMoveDlg(false)}>Cancelar</Button>
            <Button className="bg-cyan-600 hover:bg-cyan-700" onClick={submitMovement} data-testid="save-move-btn">Confirmar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {loading && <div className="fixed bottom-4 right-4 bg-white rounded-full shadow-lg p-2"><Loader2 className="h-5 w-5 animate-spin" /></div>}
    </div>
  );
}
